import React from 'react';
import { styled } from '@linaria/react';

interface ToggleSwitchProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    label?: string;
    size?: 'tiny' | 'small' | 'medium';
    hideStatusText?: boolean;
}

const ToggleContainer = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`;

const ToggleLabel = styled.span<{ $size: string }>`
    font-size: ${(props) => (props.$size === 'tiny' ? '0.7rem' : props.$size === 'small' ? '0.75rem' : '0.875rem')};
    font-weight: 600;
    color: #333;
`;

const ToggleTrack = styled.button<{ $checked: boolean; $size: string; $disabled: boolean }>`
    position: relative;
    width: ${(props) => (props.$size === 'tiny' ? '28px' : props.$size === 'small' ? '36px' : '44px')};
    height: ${(props) => (props.$size === 'tiny' ? '16px' : props.$size === 'small' ? '20px' : '24px')};
    border-radius: ${(props) => (props.$size === 'tiny' ? '8px' : props.$size === 'small' ? '10px' : '12px')};
    border: none;
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
    transition: background-color 0.2s ease;
    background-color: ${(props) => {
        if (props.$disabled) return '#ccc';
        return props.$checked ? '#28a745' : '#dc3545';
    }};
    opacity: ${(props) => (props.$disabled ? 0.6 : 1)};
    padding: 0;

    &:focus {
        outline: 2px solid #0066cc;
        outline-offset: 2px;
    }

    &:hover:not(:disabled) {
        opacity: 0.9;
    }
`;

const ToggleThumb = styled.span<{ $checked: boolean; $size: string }>`
    position: absolute;
    top: 2px;
    left: ${(props) => {
        const thumbSize = props.$size === 'tiny' ? 12 : props.$size === 'small' ? 16 : 20;
        const trackWidth = props.$size === 'tiny' ? 28 : props.$size === 'small' ? 36 : 44;
        return props.$checked ? `${trackWidth - thumbSize - 2}px` : '2px';
    }};
    width: ${(props) => (props.$size === 'tiny' ? '12px' : props.$size === 'small' ? '16px' : '20px')};
    height: ${(props) => (props.$size === 'tiny' ? '12px' : props.$size === 'small' ? '16px' : '20px')};
    border-radius: 50%;
    background-color: white;
    transition: left 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
`;

const StatusText = styled.span<{ $checked: boolean; $size: string }>`
    font-size: ${(props) => (props.$size === 'tiny' ? '0.65rem' : props.$size === 'small' ? '0.7rem' : '0.75rem')};
    font-weight: 600;
    text-transform: uppercase;
    color: ${(props) => (props.$checked ? '#155724' : '#721c24')};
    min-width: ${(props) => (props.$size === 'tiny' ? '40px' : props.$size === 'small' ? '50px' : '60px')};
`;

export const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
    checked,
    onChange,
    disabled = false,
    label,
    size = 'medium',
    hideStatusText = false,
}) => {
    const handleClick = () => {
        if (!disabled) {
            onChange(!checked);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
        }
    };

    return (
        <ToggleContainer>
            {label && <ToggleLabel $size={size}>{label}</ToggleLabel>}
            <ToggleTrack
                type="button"
                role="switch"
                aria-checked={checked}
                aria-label={label || (checked ? 'Active' : 'Inactive')}
                $checked={checked}
                $size={size}
                $disabled={disabled}
                disabled={disabled}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
            >
                <ToggleThumb $checked={checked} $size={size} />
            </ToggleTrack>
            {!hideStatusText && (
                <StatusText $checked={checked} $size={size}>
                    {checked ? 'Active' : 'Inactive'}
                </StatusText>
            )}
        </ToggleContainer>
    );
};

export default ToggleSwitch;
